/* WebSocket Service — Real-time connection to backend */
const API = process.env.REACT_APP_BACKEND_URL;

class WebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = new Map(); // type -> Set<callback>
    this.reconnectTimer = null;
    this.reconnectDelay = 2000;
    this.maxReconnectDelay = 30000;
    this.userId = 'anonymous';
  }

  connect(userId = 'anonymous') {
    this.userId = userId;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;

    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    try {
      this.ws = new WebSocket(`${wsUrl}/ws/${userId}`);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectDelay = 2000;
        this._emit('connection', { status: 'connected' });
        // Start heartbeat
        this._heartbeat = setInterval(() => {
          if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'pong') return;
          this._emit(data.type, data);
          this._emit('*', data); // wildcard listeners
        } catch { /* ignore parse errors */ }
      };

      this.ws.onclose = () => {
        clearInterval(this._heartbeat);
        this._emit('connection', { status: 'disconnected' });
        this._scheduleReconnect();
      };

      this.ws.onerror = () => {
        this.ws?.close();
      };
    } catch {
      this._scheduleReconnect();
    }
  }

  disconnect() {
    clearInterval(this._heartbeat);
    clearTimeout(this.reconnectTimer);
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  on(type, callback) {
    if (!this.listeners.has(type)) this.listeners.set(type, new Set());
    this.listeners.get(type).add(callback);
    return () => this.listeners.get(type)?.delete(callback);
  }

  _emit(type, data) {
    const cbs = this.listeners.get(type);
    if (cbs) cbs.forEach((cb) => cb(data));
  }

  _scheduleReconnect() {
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, this.maxReconnectDelay);
      this.connect(this.userId);
    }, this.reconnectDelay);
  }
}

const wsService = new WebSocketService();

// iOS Safari aggressively kills WebSocket when page goes to background
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && wsService.ws?.readyState !== WebSocket.OPEN) {
      wsService.connect(wsService.userId);
    }
  });
}

export default wsService;
